import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IonIcon } from '@ionic/react';
import { closeOutline, optionsOutline } from 'ionicons/icons';
import ProChip from './ProChip';
import ProFilterSheet from './bottom-sheet/ProFilterSheet';

interface FilterOption {
  value: string;
  label: string;
}

interface FilterDef {
  key: string;
  label: string;
  options: FilterOption[];
}

interface ProFilterChipsProps {
  filters: FilterDef[];
  activeFilters: Record<string, string>;
  onFilterChange: (key: string, value: string) => void;
}

/**
 * Pro mobile filter bar.
 *
 * Shows the current filter state as a horizontal strip of chips plus a
 * leading "Filters" trigger button. Tapping anything in the strip opens a
 * `ProFilterSheet` bottom sheet containing every filter section, which is
 * more native than the per-chip action sheet we shipped originally.
 *
 * The component's public API is unchanged (`filters`, `activeFilters`,
 * `onFilterChange`) so existing call sites in ProBillsPage, ProLegislatorsPage,
 * and ProCalendarPage do not need to be modified.
 */
const ProFilterChips: React.FC<ProFilterChipsProps> = ({
  filters,
  activeFilters,
  onFilterChange,
}) => {
  const { t } = useTranslation('pro');
  const [sheetOpen, setSheetOpen] = useState(false);

  const activeCount = Object.values(activeFilters).filter((v) => v !== '' && v != null).length;
  const hasActive = activeCount > 0;

  const handleClearAll = () => {
    filters.forEach((f) => onFilterChange(f.key, ''));
  };

  return (
    <>
      <div
        className="pro-filter-chips flex items-center gap-2 px-3 pt-2 pb-1"
        style={{ overflowX: 'auto', flexWrap: 'nowrap', WebkitOverflowScrolling: 'touch' }}
      >
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          className={`pro-filter-trigger inline-flex items-center gap-1.5 shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors touch-manipulation active:scale-[0.98] ${
            hasActive
              ? 'bg-primary-300/35 text-primary-900 border-primary-400 dark:bg-primary-800/55 dark:text-primary-100 dark:border-primary-500'
              : 'bg-white text-text-primary border-border-light dark:bg-[var(--pro-surface-card)] dark:text-text-dark-primary dark:border-white/10'
          }`}
        >
          <IonIcon icon={optionsOutline} aria-hidden="true" style={{ fontSize: 14, width: 14, height: 14 }} />
          <span className="leading-none">
            {hasActive
              ? t('bottomSheet.filtersWithCount', { count: activeCount })
              : t('bottomSheet.filters')}
          </span>
        </button>

        {filters.map((filterDef) => {
          const value = activeFilters[filterDef.key] ?? '';
          if (!value) return null;
          const option = filterDef.options.find((o) => o.value === value);
          return (
            <ProChip
              key={filterDef.key}
              onClick={() => setSheetOpen(true)}
              className="bg-primary-300/35 text-primary-900 dark:bg-primary-800/55 dark:text-primary-100 border border-primary-400/30 dark:border-primary-500/40"
            >
              {option?.label ?? value}
            </ProChip>
          );
        })}

        {hasActive && (
          <ProChip
            onClick={handleClearAll}
            icon={closeOutline}
            className="bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400"
          >
            {t('common.clear')}
          </ProChip>
        )}
      </div>

      <ProFilterSheet
        isOpen={sheetOpen}
        onDismiss={() => setSheetOpen(false)}
        filters={filters}
        activeFilters={activeFilters}
        onFilterChange={onFilterChange}
        onClearAll={handleClearAll}
      />
    </>
  );
};

export default ProFilterChips;
